import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipAriaOrder, TooltipMode } from '@magicschool/spellbook/components/Tooltip/Tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { ComponentProps } from 'react';

type TooltipTriggerSlotAriaProps = {
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

const mergeAriaIds = (tooltipId: string, existingValue: string | undefined, order: 'first' | 'last'): string => {
  if (!existingValue) return tooltipId;
  return order === 'first' ? `${existingValue} ${tooltipId}` : `${tooltipId} ${existingValue}`;
};

export const TooltipTriggerSlot = Slot<
  Omit<ComponentProps<typeof TooltipPrimitive.Trigger>, 'asChild'> & {
    tooltipId: string;
    asToggleTip?: boolean;
    mode?: TooltipMode;
    ariaOrder?: TooltipAriaOrder;
  }
>({
  displayName: 'Tooltip.Trigger',
  preventOverride: true,
  renderDefault: ({
    children,
    ref,
    tooltipId,
    mode = 'description',
    ariaOrder = 'first',
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': ariaLabelledBy,
    ...props
  }) => {
    // Build the aria attributes based on tooltip mode
    const ariaProps: TooltipTriggerSlotAriaProps = {};

    if (mode === 'label') {
      // Label mode: tooltip ID becomes aria-labelledby vs. relying on default aria-describedby behavior from Radix
      ariaProps['aria-describedby'] = ariaDescribedBy ?? '';
      ariaProps['aria-labelledby'] = mergeAriaIds(tooltipId, ariaLabelledBy, ariaOrder);
    } else {
      // Description mode: tooltip ID becomes aria-describedby (default Radix behavior)
      ariaProps['aria-labelledby'] = ariaLabelledBy ?? '';
      ariaProps['aria-describedby'] = mergeAriaIds(tooltipId, ariaDescribedBy, ariaOrder);
    }

    return (
      // `asChild` is set to true in all cases to avoid implementation conflicts
      <TooltipPrimitive.Trigger asChild data-spellbook-name="tooltip-trigger" ref={ref} {...props} {...ariaProps}>
        {typeof children === 'string' && children.length > 0 ? (
          <button type="button" className="focus-visible-ring">
            {children}
          </button>
        ) : (
          children
        )}
      </TooltipPrimitive.Trigger>
    );
  },
});

export const TooltipContentSlot = Slot<TextProps>({
  displayName: 'Tooltip.Content',
  preventOverride: true,
  renderDefault: (props) => <Text variant="caption-1" {...props} />,
});

/**
 * @TODO [SPE-47] Rename `Header` Slots to `Heading`
 * https://linear.app/magicschool/issue/SPE-47/rename-header-slots-to-heading
 */
export const TooltipHeaderSlot = Slot<TextProps>({
  displayName: 'Tooltip.Header',
  preventOverride: true,
  renderDefault: (props) => <Text data-spellbook-name="tooltip-header" variant="heading-5" {...props} />,
});
